import { initializeApp } from 'firebase/app';
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';
import { DEVICE_ID_KEY, FCM_TOKEN_KEY } from '../config/api';
import { firebaseConfig, getVapidPublicKey } from '../config/firebase';
import { emitInboxNotification, normalizeFcmPayload } from '../utils/notificationInbox';
import * as api from './api';

const firebaseApp = initializeApp(firebaseConfig);

let messagingInstance = null;

const serviceWorkerUrl = () => {
  const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return publicUrl ? `${publicUrl}/firebase-messaging-sw.js` : '/firebase-messaging-sw.js';
};

async function getMessagingInstance() {
  if (!(await isSupported())) return null;
  if (!messagingInstance) {
    messagingInstance = getMessaging(firebaseApp);
  }
  return messagingInstance;
}

export const getOrCreateDeviceId = () => {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `web-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
  } catch {
    return `web-${Date.now()}`;
  }
};

const buildRegisterPayload = (fcmToken) => ({
  device_id: getOrCreateDeviceId(),
  platform: 'web',
  device_type: 'browser',
  token: fcmToken,
  user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
});

/**
 * Request permission if needed, obtain FCM token, POST /admin/devices/register.
 * Called once from the admin shell after login (dashboard layout).
 */
export async function registerFcmOnDashboard() {
  if (typeof window === 'undefined' || !('Notification' in window)) return;

  if (Notification.permission === 'default') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;
  } else if (Notification.permission !== 'granted') {
    return;
  }

  const vapidKey = getVapidPublicKey();
  if (!vapidKey) {
    console.warn('FCM: REACT_APP_VAPID_PUBLIC_KEY is not set');
    return;
  }

  const messaging = await getMessagingInstance();
  if (!messaging) return;

  const registration = await navigator.serviceWorker.register(serviceWorkerUrl());
  const fcmToken = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });

  if (!fcmToken) return;

  localStorage.setItem(FCM_TOKEN_KEY, fcmToken);
  await api.registerDevice(buildRegisterPayload(fcmToken));
}

/** Foreground FCM messages — show a system notification when the tab is focused. */
export async function bindFcmForegroundListener() {
  const messaging = await getMessagingInstance();
  if (!messaging) return () => {};

  return onMessage(messaging, (payload) => {
    const inboxItem = normalizeFcmPayload(payload);
    emitInboxNotification(inboxItem);

    if (Notification.permission === 'granted') {
      const n = new Notification(inboxItem.from, {
        body: inboxItem.content,
        icon: payload.notification?.icon || '/favicon.png',
        data: payload.data,
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    }
  });
}

/** Unregister device on backend and revoke FCM token (logout). */
export async function unregisterFcmOnLogout() {
  const deviceId = localStorage.getItem(DEVICE_ID_KEY);
  const fcmToken = localStorage.getItem(FCM_TOKEN_KEY);

  if (deviceId || fcmToken) {
    try {
      await api.unregisterDevice({
        device_id: deviceId || getOrCreateDeviceId(),
        token: fcmToken || undefined,
      });
    } catch (err) {
      console.warn('FCM unregister API failed:', err);
    }
  }

  try {
    const messaging = await getMessagingInstance();
    if (messaging) await deleteToken(messaging);
  } catch (err) {
    console.warn('FCM deleteToken failed:', err);
  }

  localStorage.removeItem(FCM_TOKEN_KEY);
}
